import { useState, useEffect } from 'react';
import { getBroadSeedsColumn } from '../services/pmt3ProblemsDashboardService'; // Reusing service file

interface UsePmt3FarmingSeedsDataHook {
    isLoading: boolean;
    error: Error | null;
    ctaSeedsEntries: string[];
    uniqueWordsEntries: string[];
    refetch: () => void;
}

/**
 * Hook to fetch data for the PMT3 Farming Seeds Dashboard.
 */
export const usePmt3FarmingSeedsData = (
    companyName: string | null | undefined
): UsePmt3FarmingSeedsDataHook => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [ctaSeedsEntries, setCtaSeedsEntries] = useState<string[]>([]);
    const [uniqueWordsEntries, setUniqueWordsEntries] = useState<string[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    const refetch = () => setRefreshTrigger(prev => prev + 1);

    useEffect(() => {
        const loadData = async () => {
            if (!companyName) {
                setCtaSeedsEntries([]);
                setUniqueWordsEntries([]);
                setIsLoading(false);
                setError(null);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                // Fetch both columns concurrently
                const [ctaResult, uniqueResult] = await Promise.all([
                    getBroadSeedsColumn(companyName, 'ctas_and_broad_seeds'),
                    getBroadSeedsColumn(companyName, 'unique_words')
                ]);

                setCtaSeedsEntries(ctaResult);
                setUniqueWordsEntries(uniqueResult);

            } catch (err) {
                console.error("Error fetching Farming Seeds data:", err);
                setError(err instanceof Error ? err : new Error('Failed to fetch Farming Seeds data'));
                setCtaSeedsEntries([]);
                setUniqueWordsEntries([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();

    }, [companyName, refreshTrigger]); // Re-run effect if companyName or refreshTrigger changes

    return { isLoading, error, ctaSeedsEntries, uniqueWordsEntries, refetch };
};